import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import FormFiller from "@/components/FormFiller";
import { Loader2, CreditCard, ChevronDown, FileText, CheckCircle2 } from "lucide-react";
import { usePaymentData, PaymentStatusBadge, PaymentSummary, RecentPayments, QuickPayment } from "@/components/PaymentOverview";

const OPTIONS = [
  "Invoice",
  "BACS",
  "Finance",
  "Credit Card (Deposit) then Invoice",
  "Credit Card (Complete)",
  "Credit Card (Deposit) Then Finance",
  "Under Warranty",
];

// Payment type -> required form name(s). Best-effort mapping — confirm/adjust as needed.
const PAYMENT_TYPE_FORMS = {
  "Invoice": ["Deposit Payment (Invoice)"],
  "BACS": ["Payment BACS"],
  "Finance": ["Finance Application"],
  "Credit Card (Deposit) then Invoice": ["Deposit Payment (Credit Card)"],
  "Credit Card (Complete)": ["Payment (Credit Card)"],
  "Credit Card (Deposit) Then Finance": ["Deposit Payment (Credit Card)"],
  "Under Warranty": [],
};

export default function CustomerPayment({ legacyId, value, depositDate, finalDate, onSaved, quotes, onViewAll }) {
  const { can, user } = useAuth();
  const canEdit = can("orders.manage");
  const canRecord = !!user?.is_super || can("finance.manage") || can("orders.manage");
  const pay = usePaymentData(legacyId, quotes);
  const [val, setVal] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const [forms, setForms] = useState([]);
  const [subs, setSubs] = useState([]);
  const [fillId, setFillId] = useState(null);
  const [view, setView] = useState(null);
  const openSub = async (sid) => { try { setView((await api.get(`/submissions/${sid}`)).data); } catch { toast.error("Could not open the saved form"); } };

  const loadSubs = () => api.get("/submissions", { params: { task: legacyId } }).then((r) => setSubs(r.data.items || [])).catch(() => {});
  useEffect(() => {
    api.get("/forms").then((r) => setForms(r.data.forms || [])).catch(() => {});
    loadSubs();
  }, [legacyId]);

  const status = finalDate
    ? { label: "Paid in full", tone: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" }
    : depositDate
      ? { label: "Deposit paid — awaiting final payment", tone: "bg-blue-50 text-blue-700 border-blue-200", dot: "bg-blue-500" }
      : { label: "Awaiting deposit", tone: "bg-rose-50 text-rose-700 border-rose-200", dot: "bg-rose-500" };

  const change = async (v) => {
    const prev = val;
    setVal(v);
    setSaving(true);
    try {
      await api.patch(`/tasks/${legacyId}`, { payment_method: v });
      toast.success("Payment method updated");
      onSaved?.();
    } catch (e) {
      setVal(prev);
      toast.error(e.response?.data?.detail || "Could not update payment method");
    } finally { setSaving(false); }
  };

  const requiredNames = PAYMENT_TYPE_FORMS[val] || [];
  const requiredForms = requiredNames
    .map((name) => forms.find((f) => f.name === name) || { id: null, name })
    .map((f) => { const s = subs.find((x) => x.form_name === f.name); return { ...f, done: !!s, subId: s?.id, amount: s?.answers?.amount }; });

  return (
    <section className="bg-white border border-slate-200 rounded-[8px] shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5 space-y-4" data-testid="task-customer-payment">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-indigo-500" /> Customer Payment
        </h2>
        {pay.status ? <PaymentStatusBadge status={pay.status} /> : (
          <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium ${status.tone}`} data-testid="customer-payment-status">
            <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} /> {status.label}
          </span>
        )}
      </div>
      <PaymentSummary p={pay} legacyOnly={!!(depositDate || finalDate) && !pay.payments?.length} />
      <div>
      <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
        How Is This Customer Paying? <span className="text-rose-500">*</span>
      </label>
      {canEdit ? (
        <div className="relative mt-1">
          <select value={val} onChange={(e) => change(e.target.value)} disabled={saving} data-testid="customer-payment-select"
            className="block w-full appearance-none rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 pr-9 text-sm text-slate-900 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 focus:outline-none disabled:opacity-60">
            <option value="">Please select</option>
            {OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          {saving
            ? <Loader2 className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
            : <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />}
        </div>
      ) : (
        <div className="mt-1 text-sm text-slate-800 font-medium" data-testid="customer-payment-readonly">{val || "Not set"}</div>
      )}
      </div>

      <RecentPayments p={pay} depositDate={depositDate} finalDate={finalDate} onViewAll={onViewAll} />
      {canRecord && <QuickPayment p={pay} legacyId={legacyId} onSaved={onSaved} />}

      {val && (
        <div className="pt-4 border-t border-slate-100" data-testid="customer-payment-forms">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Forms to complete</div>
          {requiredForms.length === 0 ? (
            <div className="text-xs text-slate-400">No forms configured for this payment type yet.</div>
          ) : (
            <ul className="space-y-1.5">
              {requiredForms.map((f) => (
                <li key={f.name}>
                  <button
                    disabled={!f.id && !f.subId}
                    onClick={() => (f.subId ? openSub(f.subId) : f.id && setFillId(f.id))}
                    className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-sm text-left ${f.done ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200 hover:border-indigo-300"} disabled:opacity-60`}
                    data-testid={`payment-form-${f.name.replace(/\s+/g, "-").toLowerCase()}`}>
                    {f.done ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : <FileText className="w-4 h-4 text-slate-400 shrink-0" />}
                    <span className="flex-1 min-w-0 text-slate-700">{f.name}</span>
                    <span className="text-xs shrink-0 text-slate-400" data-testid={`payment-form-status-${f.name.replace(/\s+/g, "-").toLowerCase()}`}>{f.done ? `${f.amount ? `£${Number(f.amount).toLocaleString("en-GB", { minimumFractionDigits: 2 })} · ` : ""}Completed · View` : f.id ? "To complete" : "Not built yet"}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {fillId && <FormFiller formId={fillId} context={{ task_legacy_id: legacyId }} onClose={() => setFillId(null)} onSubmitted={() => { setFillId(null); loadSubs(); pay.reload(); onSaved?.(); }} />}
      {view && <FormFiller submission={view} context={{ task_legacy_id: legacyId }} mapTaskId={legacyId} onClose={() => setView(null)} />}
    </section>
  );
}
